import { useTranslation } from "react-i18next";
import { Page } from "@bfme-technology/spa";
import type { SettingsPageProps } from "./SettingsPage.types";
import { useSettings } from "./SettingsPage.hook";
import { SettingsProfileForm } from "../SettingsProfileForm";
import { SettingsPasswordForm } from "../SettingsPasswordForm";
import * as styles from "./SettingsPage.styles";

export const SettingsPage: React.FC<SettingsPageProps> = ({
  currentName,
  currentEmail,
  user,
}) => {
  const { t } = useTranslation();
  const settings = useSettings(currentName, currentEmail, user);

  return (
    <Page title={t("settings", "Settings")} data-testid="settings-page">
      <p className={styles.subtitle}>
        {t(
          "settingsSubtitle",
          "Manage your account settings, preferences, and security.",
        )}
      </p>

      <div className={styles.gridContainer}>
        {/* Navigation & Info Sidebar */}
        <div className={styles.sidebarContainer}>
          {/* User Profile Card */}
          <div className={styles.profileCard}>
            <div className={styles.avatar}>
              {settings.userInitials}
            </div>
            <h2 className={styles.profileName}>
              {settings.name}
            </h2>
            <p className={styles.profileEmail}>{settings.email}</p>
            {user?.is_verified && (
              <span className={styles.verifiedBadge}>
                <i className={styles.verifiedIcon} />
                Verified Account
              </span>
            )}
          </div>

          {/* Sidebar Tabs */}
          <div className={styles.tabsContainer}>
            <button
              onClick={() => settings.setActiveTab("profile")}
              className={styles.getTabClass(settings.activeTab === "profile")}
            >
              <i className={`fa-solid fa-user-gear ${styles.tabIcon}`} />
              {t("editProfile", "Edit Profile")}
            </button>
            <button
              onClick={() => settings.setActiveTab("password")}
              className={styles.getTabClass(settings.activeTab === "password")}
            >
              <i className={`fa-solid fa-shield-halved ${styles.tabIcon}`} />
              {t("changePassword", "Change Password")}
            </button>
          </div>
        </div>

        {/* Tab Content Panel */}
        <div className={styles.contentPanel}>
          {settings.activeTab === "profile" ? (
            <SettingsProfileForm
              firstName={settings.firstName}
              setFirstName={settings.setFirstName}
              lastName={settings.lastName}
              setLastName={settings.setLastName}
              name={settings.name}
              setName={settings.setName}
              email={settings.email}
              setEmail={settings.setEmail}
              phone={settings.phone}
              setPhone={settings.setPhone}
              dob={settings.dob}
              setDob={settings.setDob}
              gender={settings.gender}
              setGender={settings.setGender}
              culture={settings.culture}
              setCulture={settings.setCulture}
              currency={settings.currency}
              setCurrency={settings.setCurrency}
              updatingProfile={settings.updatingProfile}
              handleUpdateProfile={settings.handleUpdateProfile}
            />
          ) : (
            <SettingsPasswordForm
              currentPassword={settings.currentPassword}
              setCurrentPassword={settings.setCurrentPassword}
              newPassword={settings.newPassword}
              setNewPassword={settings.setNewPassword}
              changingPassword={settings.changingPassword}
              handleChangePassword={settings.handleChangePassword}
            />
          )}
        </div>
      </div>
    </Page>
  );
};

export default SettingsPage;
